<template>
  <div>
    <div :id="id">
      <div class="view-receipt">
        <div class="A5">
          <div class="header px-4 pt-2 pb-2">
            <h3 class="receipt-title">{{ t("title") }}</h3>
            <div class="row no-gutters header-row">
              <div class="col clinic-block">
                <span class="clinic-name">{{ branchName }}</span>
                <span
                  v-if="!isEmpty(clinicAddress)"
                  class="clinic-address d-block"
                  style="white-space: pre-line;"
                  >{{ clinicAddress }}</span
                >
                <span
                  v-if="!isEmpty(clinicTelephone)"
                  class="clinic-telephone d-block"
                  >{{ t("tel") }} {{ clinicTelephone }}</span
                >
              </div>
              <div class="col-auto document-info">
                <h5
                  class="receipt-type"
                  :class="{ cancelled: mode === 'cancel' }"
                >
                  {{ receiptType }}
                </h5>
                <div class="doc-line">
                  <span class="doc-label">{{ t("receipt-no") }}</span>
                  <span class="receipt-no">{{ receipt.receiptNo }}</span>
                </div>
                <div class="doc-line">
                  <span class="doc-label">{{ t("date") }}</span>
                  <span class="date">{{ receipt.createDate }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="body px-4">
            <table class="w-100 receipt-layout">
              <thead>
                <tr>
                  <td>
                    <div class="header-space">&nbsp;</div>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div class="row patient-info">
                      <div class="col text-left">
                        <p>
                          <span class="field-label">{{ t("patient-name") }}</span>
                          <span class="patient-name">{{ patientName }}</span>
                          <span class="field-label pl-3">{{ t("dn") }}</span>
                          <span class="patient-dn">{{ patient.dn }}</span>
                        </p>
                        <p>
                          <span class="field-label">{{ t("address") }}</span>
                          <span>{{ patientAddress }}</span>
                        </p>
                      </div>
                      <div
                        v-if="!isEmpty(nextVisit) && !isEmpty(nextVisit.start)"
                        class="col-auto text-right next-visit"
                      >
                        <div class="next-visit-title">{{ t("next-app") }}</div>
                        <div class="next-visit-date">{{ nextVisitDateTime }}</div>
                        <div
                          v-if="!isEmpty(nextVisitDetail)"
                          class="next-visit-detail"
                        >
                          {{ nextVisitDetail }}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="row payment-table">
                      <div class="col">
                        <b-table
                          table-class="table-black"
                          :bordered="false"
                          :items="receipt.items"
                          :fields="visibleFields"
                        >
                          <template #cell(finalDiscount)="row">{{
                            discountCellValue(row.item)
                          }}</template>
                          <template #cell(amount)="row">
                            <div>{{ row.value }}</div>
                            <div v-if="row.item.remained" class="item-balance">
                              ({{ t("overdue") }} {{ formatCurrency(row.item.remained) }})
                            </div>
                          </template>
                          <template #custom-foot>
                            <tr>
                              <td
                                v-for="field in visibleFields"
                                :key="field.key"
                                :class="totalCellClass(field)"
                              >
                                {{ totalCellValue(field) }}
                              </td>
                            </tr>
                          </template>
                        </b-table>
                      </div>
                    </div>
                    <div class="payment-summary">
                      <div class="paid-by-col">
                        <template v-if="isSinglePayment">
                          <div class="pay-line single">
                            <span class="pay-title">{{ t("paid-by") }}</span>
                            <span class="pay-method">{{
                              getCateName(singlePayment, lang)
                            }}</span>
                            <span class="pay-leader"></span>
                            <span class="pay-amount">{{
                              formatCurrency(singlePayment.amount)
                            }}</span>
                          </div>
                        </template>
                        <template v-else>
                          <div class="pay-title">{{ t("paid-by") }}</div>
                          <div
                            v-for="category in receipt.displayAmount"
                            :key="category.id"
                            class="pay-line"
                          >
                            <span class="pay-method">{{
                              getCateName(category, lang)
                            }}</span>
                            <span class="pay-leader"></span>
                            <span class="pay-amount">{{
                              formatCurrency(category.amount)
                            }}</span>
                          </div>
                        </template>
                      </div>
                      <div class="totals-col">
                        <div v-if="isDiscounted" class="saved-line">
                          <span class="saved-label">{{
                            t("total-discount")
                          }}</span>
                          <span class="saved-value">{{
                            formatCurrency(receipt.totals.finalDiscount)
                          }}</span>
                        </div>
                        <div class="total-line">
                          <span class="total-label">{{ t("total-price") }}</span>
                          <span class="total-value">{{
                            formatCurrency(receipt.amount)
                          }}</span>
                        </div>
                        <div class="paid-line">
                          <span class="paid-label">{{ t("pay-today") }}</span>
                          <span class="paid-value">{{
                            formatCurrency(receipt.paid)
                          }}</span>
                        </div>
                        <div class="paid-words">
                          ( {{ formatWordCurrency(receipt.paid, lang) }} )
                        </div>
                        <div v-show="receipt.overdue" class="overdue-line">
                          <span class="overdue-label">{{ t("overdue") }}</span>
                          <span class="overdue-value">{{
                            formatCurrency(receipt.overdue)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <div class="footer-space">&nbsp;</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="footer px-4 pt-0 pb-3">
            <div class="row signature-row">
              <div class="col-4">
                <p class="signature">{{ t("service-recipient") }}</p>
              </div>
              <div class="col-4 offset-4">
                <p class="signature">{{ t("financial-officer") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import moment from "moment";
import { formatCurrency as formatCurrencyUtil } from "@/utils/utils";
import THBText from "thai-baht-text";
import USDText from "dollars-to-words";
import { cloneDeep, trim, get, isEmpty, uniqueId } from "lodash";
import { mapActions, mapGetters } from "vuex";
// import Logo from "@/components/Logo";
import Loading from "@/components/Loading";
import { eventBus } from "@/main";
import "@/theme/ReceiptPreview.scss";
import { formatPhoneForDisplay } from "@/utils/phoneUtils";
import { receiptSaveFeedback } from "@/utils/receiptSaveFeedback";
import $ from "jquery";

import Dialog from "@/components/modal/Dialog";

// Typographic glyphs from the design, not ASCII lookalikes: a real minus
// sign aligns with tabular figures, and the en dash is the empty marker.
const MINUS_SIGN = "−";
const EN_DASH = "–";

export default {
  name: "ReceiptView",
  components: {
    Loading,
    // Logo,
    Dialog,
  },
  props: {
    mode: String,
    lang: String,
    containerTarget: String,
    afterPrint: Function,
    createReceiptSuccess: Function,
    patientData: Object,
    receiptData: Object,
    displayKeys: Array,
    branchData: Object,
  },
  data() {
    return {
      id: uniqueId("receipt-"),
      isLoading: false,
      isCreateReceipt: false,
      mediaPrint: null,
      printCount: 0,
      logo: null,
      nextVisit: {},
      patient: {},
      receipt: {
        receiptNo: "",
        amount: 0,
        paid: 0,
        overdue: 0,
        displayAmount: [],
        items: [],
        // Text columns read left, figures read right — see the token sheet in
        // docs/superpowers/specs/2026-09-11-receipt-redesign-handoff.md
        fields: [
          {
            key: "name",
            label: this.t("list"),
            thClass: ["text-left"],
            tdClass: ["item-name"],
            visible: true,
          },
          {
            key: "doctor",
            label: this.t("doctor"),
            thStyle: { width: "200px" },
            thClass: ["text-left"],
            tdClass: ["item-doctor"],
            visible: true,
          },
          {
            key: "quantity",
            label: this.t("quantity"),
            thStyle: { width: "50px" },
            thClass: ["text-right"],
            tdClass: ["text-right"],
            visible: true,
          },
          {
            key: "price",
            label: this.t("price"),
            thStyle: { width: "100px" },
            thClass: ["text-right"],
            tdClass: ["text-right", "item-price"],
            visible: true,
          },
          {
            key: "finalDiscount",
            label: this.t("discount"),
            thStyle: { width: "100px" },
            thClass: ["text-right"],
            tdClass: (value, key, item) => [
              "text-right",
              this.hasItemDiscount(item)
                ? "item-discount--some"
                : "item-discount--none",
            ],
            visible: true,
          },
          {
            key: "amount",
            label: this.t("net-price"),
            thStyle: { width: "96px" },
            thClass: ["text-right"],
            tdClass: ["text-right"],
            visible: true,
          },
        ],
        // Filled in created() from the raw (unformatted) item figures.
        totals: { quantity: 0, price: 0, finalDiscount: 0, amount: 0 },
        hasDiscount: false,
      },
    };
  },
  created() {
    if (window.matchMedia) {
      this.mediaPrint = window.matchMedia("print");
    }
    this.patient = this.patientData;
    this.receipt.receiptNo = get(this.receiptData, "receiptNo", "-");
    this.receipt.createDate = this.getDateTime(
      get(this.receiptData, "creationDt", moment().format()),
      this.lang
    );
    this.receipt.amount = this.receiptData.amount;
    this.receipt.paid = this.receiptData.paid;
    this.receipt.overdue = this.receiptData.overdue;
    this.receipt.displayAmount = this.receiptData.displayAmount; // Display Payment Methods
    const totals = { quantity: 0, price: 0, finalDiscount: 0, amount: 0 };
    this.receipt.items = cloneDeep(
      this.receiptData.receiptItems || // receiptItems จะส่งมาใน payment หรือ api reprint จาก backend node ตั่วใหม่
        this.receiptData.item || // item จะถูกส่งมาจาก api reprint ที่ถูกสร้างจากตัวเก่า
        []
    ).map(item => {
      delete item._rowVariant;
      if (item.quantity == null) {
        item.quantity = "";
      }
      if (item.treatmentItem) {
        // api reprint ตัวใหม่ จะส่งรายละเอียด nest อยู่ใน treatmentItem
        item.name =
          this.lang == "th"
            ? item.treatmentItem.name
            : item.treatmentItem.nameEn || item.treatmentItem.name;
        const { fullNameEn, fullName } = this.getUserListIncludeDeletedBy(
          item.treatmentItem.actionUid,
          "uid"
        );
        item.doctor = this.lang == "th" ? fullName : fullNameEn || fullName;
      } else {
        item.name = this.lang == "th" ? item.name : item.nameEn || item.name;
        item.doctor =
          this.lang == "th" ? item.doctor : item.doctorEn || item.doctor;
      }

      // Sum the raw figures here: the mapped item below carries formatted
      // strings such as "1,100.00", which Number() turns into NaN.
      totals.quantity += this.toNumber(item.quantity);
      totals.price += this.toNumber(item.price);
      totals.finalDiscount += this.toNumber(item.finalDiscount);
      totals.amount += this.toNumber(item.amount);

      return {
        ...item,
        // name: treatmentName,
        // doctor: doctorName,
        price: this.formatCurrency(item.price),
        finalDiscount: this.formatCurrency(item.finalDiscount),
        amount: this.formatCurrency(item.amount),
      };
    });
    this.receipt.totals = totals;
    this.receipt.hasDiscount = totals.finalDiscount > 0;

    if (this.mode == "create") {
      this.loadNextVisit();
    } else {
      this.nextVisit = get(this.receiptData, "nextVisit", {});
    }
    const activeBranchInfo = this.branchData || this.getBranchInfo;
    this.logo = this.viewFile(get(activeBranchInfo, "clinic.logo"));
  },
  mounted() {
    this.updatePrint();
  },
  beforeDestroy() {
    $(this.containerTarget).empty();
    $("body").removeClass("print-content-modal");
    this.enabledMediaQueryListener(false);
  },
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleUser/getBranchInfo",
      getTreatmentList: "moduleAppointment/getTreatmentList",
      viewFile: "moduleContent/viewFile",
      getUserListIncludeDeletedBy: "moduleUser/getUserListIncludeDeletedBy",
    }),
    activeBranchInfo() {
      return this.branchData || this.getBranchInfo;
    },
    visibleFields() {
      let permanentFields = ["name", "amount"];
      if (this.isDiscounted)
        permanentFields = permanentFields.concat(["price", "finalDiscount"]);
      return this.receipt.fields.filter(field => {
        return this.displayKeys?.concat(permanentFields).includes(field.key);
      });
    },
    isDiscounted() {
      // Computed in created() from the raw figures. Reading it back off the
      // mapped items would miss any discount of 1,000 or more, because those
      // are already formatted with a thousands separator by then.
      return this.receipt.hasDiscount;
    },
    receiptType() {
      return (this.mode == "create" &&
        this.isCreateReceipt &&
        this.printCount == 0) ||
        this.mode === "original"
        ? this.t("original")
        : this.mode == "cancel"
        ? this.t("cancel")
        : this.mode == "copy" || this.printCount > 0
        ? this.t("copy")
        : this.t("example");
    },
    branchName() {
      const branchName = get(this.activeBranchInfo, "name") || "";
      return this.lang == "th"
        ? branchName
        : get(this.activeBranchInfo, "nameEn") || branchName;
    },

    clinicName() {
      const clinicName = get(this.activeBranchInfo, "clinic.name") || "";
      return this.lang == "th"
        ? clinicName
        : get(this.activeBranchInfo, "clinic.nameEn") || clinicName;
    },

    clinicAddress() {
      const clinicAddress = trim(get(this.activeBranchInfo, "address")) || "";
      return this.lang == "th"
        ? clinicAddress
        : trim(get(this.activeBranchInfo, "addressEn")) || clinicAddress;
    },

    clinicTelephone() {
      return get(this.activeBranchInfo, "tel") || "";
    },
    patientName() {
      return this.getPatientName(this.patient, this.lang);
    },
    patientAddress() {
      const { address, subDistrict, district, province, postcode } =
        this.patient;

      const text = [address, subDistrict, district, province, postcode]
        .filter(Boolean)
        .join(" ");

      return text || "-";
    },
    currentDate() {
      return this.getDate(moment().format(), this.lang);
    },
    currentTime() {
      return this.getTime(moment().format(), this.lang);
    },
    currentDateTime() {
      return this.getDateTime(moment().format(), this.lang);
    },
    nextVisitDateTime() {
      return !isEmpty(this.nextVisit.start)
        ? this.getFullDateTime(this.nextVisit.start, this.lang)
        : "";
    },
    nextVisitTreatmentTitle() {
      return this.lang == "th"
        ? this.nextVisit.treatmentTitle
        : this.nextVisit.treatmentTitleEn || this.nextVisit.treatmentTitle;
    },
    nextVisitDoctorName() {
      return this.lang == "th"
        ? this.nextVisit.doctorName
        : this.nextVisit.doctorNameEn || this.nextVisit.doctorName;
    },
    nextVisitDetail() {
      return [this.nextVisitTreatmentTitle, this.nextVisitDoctorName]
        .filter(Boolean)
        .join(" · ");
    },
    paymentMethods() {
      return this.receipt.displayAmount || [];
    },
    // A lone payment method renders inline after the "ชำระโดย" label, so the
    // strip never reads as a list heading with one orphan row beneath it.
    isSinglePayment() {
      return this.paymentMethods.length === 1;
    },
    singlePayment() {
      return this.paymentMethods[0] || { cateName: "", amount: 0 };
    },
  },
  methods: {
    ...mapActions({
      createReceipt: "moduleReceipt/createReceipt",
      fetchNextVisitAppointmentByPatientId:
        "moduleAppointment/fetchNextVisitAppointmentByPatientId",
      fetchBranchByUrl: "moduleBranch/fetchBranchByUrl",
    }),
    t(path) {
      const trans = {
        th: {
          title: "ใบเสร็จรับเงิน",
          dn: "DN",
          original: "ต้นฉบับ",
          cancel: "ยกเลิก",
          copy: "สำเนา",
          example: "ตัวอย่าง",
          overdue: "ค้างชำระ",
          remain: "ค้าง",
          "pay-today": "ชำระวันนี้",
          "paid-by": "ชำระโดย",
          "total-price": "ราคารวมสุทธิ",
          dentist: "ทันตแพทย์",
          treatment: "การรักษา",
          "patient-name": "ชื่อ",
          date: "วันที่",
          "receipt-no": "เลขที่ใบเสร็จ",
          tel: "โทร",
          "next-app": "นัดหมายถัดไป",
          "service-recipient": "ผู้รับบริการ",
          "financial-officer": "เจ้าหน้าที่การเงิน",
          list: "รายการ",
          sum: "รวม",
          "total-discount": "ส่วนลดรวม",
          doctor: "แพทย์",
          quantity: "จำนวน",
          price: "ราคา",
          discount: "ส่วนลด",
          "net-price": "ราคาสุทธิ",
          address: "ที่อยู่",
        },
        en: {
          title: "RECEIPT",
          dn: "DN",
          original: "Original",
          cancel: "Cancel",
          copy: "Copy",
          example: "Example",
          overdue: "Overdue",
          remain: "remain",
          "pay-today": "Paid",
          "paid-by": "Paid By",
          "total-price": "Total Net Price",
          dentist: "Dentist",
          treatment: "Treatment",
          "patient-name": "Name",
          date: "Date",
          "receipt-no": "Receipt No.",
          tel: "Tel",
          "next-app": "Next Appointment",
          "service-recipient": "Service Recipient",
          "financial-officer": "Financial Officer",
          list: "Description",
          sum: "Total",
          "total-discount": "Total Discount",
          doctor: "Doctor",
          quantity: "Quantity",
          price: "Price",
          discount: "Discount",
          "net-price": "Net Price",
          address: "Address",
        },
      };
      return get(trans, `${this.lang || "th"}.${path}`) || path;
    },
    async print(isPaotang) {
      if (!this.isLoading) {
        if (this.mode == "create") {
          this.generateReceipt(() => {
            if (isPaotang) {
              this.afterPrint();
              return;
            }
            this.preparePrint();
          });
        } else {
          this.preparePrint();
        }
      } else {
        eventBus.$emit("alertToast", {
          message: "กำลังเตรียมพิมพ์ กรุณารอสักครู่",
          variant: "warning",
        });
      }
    },
    updatePrint() {
      $(this.containerTarget)
        .empty()
        .html($(`#${this.id} .view-receipt`).clone());
      $("body").addClass("print-content-modal");
    },
    preparePrint() {
      $(this.containerTarget)
        .empty()
        .html($(`#${this.id} .view-receipt`).clone());
      const logoImg = $(this.containerTarget).find(".logo img");
      if (!isEmpty(this.logo) && logoImg.length > 0) {
        const self = this;
        logoImg
          .on("load", function () {
            self.previewPrint();
          })
          .on("error", function () {
            self.previewPrint();
          });
      } else {
        this.previewPrint();
      }
    },
    previewPrint() {
      if (this.$device.browser.safari && this.printCount >= 1) {
        eventBus.$emit("alertSwal", {
          title: "การสั่งพิมพ์ใบเสร็จ",
          message: `ท่านสามารถใช้คำสั่ง "พิมพ์" หรือ "${
            this.$device.os.mac || this.$device.os.ios ? "Cmd" : "Ctrl"
          } + P" จากเบราว์เซอร์ได้โดยตรง หากการสั่งพิมพ์ใบเสร็จถูกปิดกั้น`,
          icon: "info",
          callback: result => {
            if (result.isConfirmed) {
              this.processPrint();
            }
          },
        });
      } else {
        this.processPrint();
      }
    },
    processPrint() {
      let pos = document.body.style.position;
      if (pos === "fixed") {
        document.body.style.position = "unset";
      }
      if (document.queryCommandSupported("print")) {
        if (!this.$device.browser.safari && this.mediaPrint) {
          this.enabledMediaQueryListener(true);
        }
        if (!document.execCommand("print")) {
          window.print();
        }
      }
      if (pos === "fixed") {
        document.body.style.position = "fixed";
      }
      this.printCount += 1;
    },
    enabledMediaQueryListener(isEnabled) {
      if (this.mediaPrint) {
        if (isEnabled) {
          this.mediaPrint.addEventListener("change", this.checkMediaQuery);
        } else {
          this.mediaPrint.removeEventListener("change", this.checkMediaQuery);
        }
      }
    },
    checkMediaQuery(e) {
      if (!e.matches) {
        if (this.afterPrint) {
          this.afterPrint();
        }
        this.enabledMediaQueryListener(false);
      }
    },
    loadLogoCallback(src) {
      this.logo = src;
    },
    async generateReceipt(callback) {
      this.$forceUpdate();

      const receipt = { ...this.receiptData };

      let data = this.formatReceiptData(receipt);

      if (this.receipt.receiptNo == "-") {
        const clinicUrl = this.$route.params.clinicUrl;
        const branchUrl = this.$route.params.branchUrl;
        this.isLoading = true;
        this.createReceipt({
          data,
          clinicUrl,
          branchUrl,
        })
          .then(async res => {
            if (res.data === "APPT-IS-NOT-UPTO-DATE" && res.status === 200) {
              await this.$refs.Dialog.showAlertInfo(
                "มีการแก้ไขข้อมูลชำระเงิน",
                "กรุณาทำรายการชำระเงินใหม่",
                "warning"
              )
                .then(() => {
                  this.$bvModal.hide("PaymentReceiptPreviewModal");
                  this.$bvModal.hide("PaymentMethodModal");
                  this.$bvModal.hide("PaymentModal");
                })
                .finally(() => {
                  throw res.data;
                });
            }
            this.receipt.receiptNo = get(res, "data.receipt.receiptNo", "-");
            this.receipt.createDate = this.getDateTime(
              get(res, "data.creationDt"),
              "th"
            );
            // LINE status AND any receipt-level discount the server could not
            // apply — the latter used to be dropped in silence. Composed in
            // receiptSaveFeedback so both are reported even when both happen.
            eventBus.$emit("alertToast", receiptSaveFeedback(res.data));
            if (this.createReceiptSuccess) {
              this.isCreateReceipt = true;
              this.createReceiptSuccess();
            }
            this.$nextTick(() => {
              if (callback) {
                callback();
              }
            });
          })
          .catch(err => {
            console.error("Error:", err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        callback();
      }
    },
    async loadNextVisit() {
      const clinicUrl = this.$route.params.clinicUrl;
      const branchUrl = this.$route.params.branchUrl;
      const patientId = this.patient.id;
      this.isLoading = true;

      this.fetchNextVisitAppointmentByPatientId({
        clinicUrl,
        branchUrl,
        patientId,
      })
        .then(res => {
          this.nextVisit = res;
          this.$nextTick(() => {
            this.updatePrint();
          });
        })
        .catch(err => {
          console.error("Error:", err);
          this.nextVisit = {};
        })
        .finally(() => {
          this.isLoading = false;
          this.receiptData.nextVisit = this.nextVisit;
        });
    },
    isEmpty(value) {
      return isEmpty(value);
    },
    // Tolerates null, "", and already-formatted "1,100.00" strings.
    toNumber(value) {
      if (value === null || value === undefined || value === "") return 0;
      const parsed =
        typeof value === "number"
          ? value
          : parseFloat(String(value).replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    },
    hasItemDiscount(item) {
      return this.toNumber(item && item.finalDiscount) > 0;
    },
    // A discount reads as a deduction ("−100.00"); no discount collapses to a
    // dash so the column stays quiet on the rows that do not matter.
    discountCellValue(item) {
      return this.hasItemDiscount(item)
        ? `${MINUS_SIGN}${item.finalDiscount}`
        : EN_DASH;
    },
    totalCellClass(field) {
      if (field.key === "name") return "total-row-label";
      if (field.key === "quantity") return "text-right total-qty";
      if (field.key === "finalDiscount") return "text-right total-discount";
      return ["price", "amount"].includes(field.key) ? "text-right" : "";
    },
    totalCellValue(field) {
      const totals = this.receipt.totals || {};
      switch (field.key) {
        case "name":
          return this.t("sum");
        case "quantity":
          // Quantities are whole units; drop the float noise rather than
          // running them through the currency formatter.
          return String(Number(this.toNumber(totals.quantity).toFixed(2)));
        case "price":
          return this.formatCurrency(totals.price);
        case "finalDiscount":
          // Same rule as the item cells: no discount collapses to a dash
          // instead of printing a meaningless "−0.00".
          return this.toNumber(totals.finalDiscount) > 0
            ? `${MINUS_SIGN}${this.formatCurrency(totals.finalDiscount)}`
            : EN_DASH;
        case "amount":
          return this.formatCurrency(totals.amount);
        default:
          return "";
      }
    },
    formatCurrency: value => {
      return formatCurrencyUtil(value);
    },
    formatWordCurrency: (value, language = "th") => {
      if (Number.isNaN(value)) return;
      if (language == "th") {
        return THBText(value) || "ศูนย์บาทถ้วน";
      } else {
        return USDText(value)
          .replace("dollar", "baht")
          .replace("cent", "satang")
          .toUpperCase();
      }
    },
    getCateName: (category, language = "th") => {
      let cateName = category.cateName;
      if (language != "th") {
        cateName = category.cateNameEn || cateName;
      }
      if (category.debtorName) {
        cateName += ` (${category.debtorName})`;
      }
      return cateName;
    },
    getPatientName: (patient, language = "th") => {
      const patientName = trim(
        `${trim(patient.titleNameTh)} ${patient.firstNameTh} ${
          patient.lastNameTh
        }`
      );
      if (language == "th") {
        return patientName;
      } else {
        return (
          trim(
            `${trim(patient.titleNameEn || patient.titleNameTh)} ${
              patient.firstNameEn || patient.firstNameTh
            } ${patient.lastNameEn || patient.lastNameTh}`
          ) || patientName
        );
      }
    },
    getDate: (dateTime, language = "th") => {
      moment.locale(language);
      const year =
        parseInt(moment(dateTime).format("YYYY")) +
        (language == "th" ? 543 : 0);
      const month = moment(dateTime).format("MMM");
      const date = moment(dateTime).format("D");
      return `${date} ${month} ${year.toString().substr(2)}`;
    },
    getTime: (dateTime, language = "th") => {
      moment.locale(language);
      const time = moment(dateTime).format("HH:mm");
      return `${time}`;
    },
    getDateTime: (dateTime, language = "th") => {
      moment.locale(language);
      const year =
        parseInt(moment(dateTime).format("YYYY")) +
        (language == "th" ? 543 : 0);
      const dateMonth = moment(dateTime).format("D MMM");
      const time = moment(dateTime).format("HH:mm");
      return `${dateMonth} ${year.toString().substr(2)} ${time}`;
    },
    getFullDateTime: (dateTime, language = "th") => {
      moment.locale(language);
      const year =
        parseInt(moment(dateTime).format("YYYY")) +
        (language == "th" ? 543 : 0);
      const dateMonth = moment(dateTime).format("ddd D MMM");
      const time = moment(dateTime).format("HH:mm");
      return `${dateMonth} ${year.toString().substr(2)} ${time}`;
    },
    formatPhoneNumber: phone => {
      if (phone) {
        return formatPhoneForDisplay(phone);
      } else {
        return "-";
      }
    },
    isNumberEmpty: value => {
      if (value > 0 && !isNaN(value)) {
        return false;
      } else {
        return true;
      }
    },
    formatReceiptData(data) {
      return {
        appointmentId: parseInt(data.appointmentId),
        treatment: { id: data.treatmentId, modifyDt: data.treatmentModifyDate },
        receipt: { ...data },
        nextVisit: this.nextVisit,
        displayAmount: this.receiptData.displayAmount,
      };
    },
  },
};
</script>

<style></style>